/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Component } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { bindActionCreators } from 'redux';
import { gnoment } from 'shared-ui/gnoment';
import {
    ContentBlock,
    NotificationActions,
    NotificationContainer,
    NotFoundPage,
    Stack,
} from 'sarsaparilla';
import { withCompatRouter, CompatRouterProps } from 'sarsaparilla/legacy';

import { INVENTORY_TREE_PERMIT } from 'ui-field-sales/src/constants/types';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';
import Banner from '../components/common/Banner';
import Header from '../components/TreePermitDetailsPage/Header';
import CTABar from '../components/TreePermitDetailsPage/CTABar';
import Content from '../components/TreePermitDetailsPage/Content';
import { fetchPermit } from '../actions/permit';
import { fetchSeasons } from '../actions/seasons';
import { fetchAvailability } from '../actions/availability';
import { fetchPermitTypes } from '../actions/permitTypes';
import {
    permitSelectors as locationsSel,
    seasonsSelector as seasonSel,
    permitTypesSelectors as typesSel,
    availabilitySelectors as availabilitySel,
} from '../reducers';
import { getParentAsset } from '../api';
import { onError } from '../utils';
import type { RootState, AppDispatch } from '../../dev/store';
import type {
    AvailabilityEntry,
    PermitType,
    RatingData,
    SeasonData,
    TreePermitData,
} from '../../types/permit';
import type { AsyncState } from '../../types/async';

// Thunk action creators return a function when called, but after dispatch
// (via bindActionCreators + thunk middleware) they resolve to a Promise.
type DispatchedThunk<TArgs extends unknown[], TReturn> = (
    ...args: TArgs
) => Promise<TReturn>;

interface TreePermitDetailsPageProps {
    router: CompatRouterProps;
    getTreePermit: DispatchedThunk<[string], { id: string; parent_asset_id: string }>;
    getSeasons: DispatchedThunk<[string, string], Record<string, unknown>>;
    getPermitTypes: DispatchedThunk<[string, boolean], Record<string, unknown>>;
    getAvailability: DispatchedThunk<[string, string], Record<string, unknown>>;
    seasons: Record<string, SeasonData>;
    treePermit: TreePermitData;
    permitTypes: Array<PermitType>;
    rating: RatingData;
    treePermitObj: AsyncState;
    seasonsObj: AsyncState;
    setNotification: (type: string, message: string) => void;
    availability: Record<string, Partial<AvailabilityEntry>>;
}

export class TreePermitDetailsPage extends Component<TreePermitDetailsPageProps> {
    state = {
        parentLocation: [],
    };

    componentDidMount() {
        const { getTreePermit, getSeasons, getPermitTypes, getAvailability } = this.props;
        const treePermitId = this.props.router?.params?.treePermitId ?? '';
        const year = gnoment().format('YYYY');
        const permitTypesOnSaleOnly = false;
        getTreePermit(treePermitId)
            .then(({ id, parent_asset_id }: { id: string; parent_asset_id: string }) => {
                Promise.all([
                    getSeasons(id, year),
                    getPermitTypes(id, permitTypesOnSaleOnly),
                    getAvailability(id, year),
                ])
                    .then(() => {
                        getParentAsset(parent_asset_id)
                            .then(({ data: { results = [] } }) =>
                                this.setState({ parentLocation: results })
                            )
                            .catch((e: { message?: string }) => this.notifyError(e));
                    })
                    .catch((e) => this.notifyError(e));
            })
            .catch((e: { message?: string }) => this.notifyError(e));
    }

    notifyError = (e: { message?: string }) => {
        const { setNotification } = this.props;
        const err = onError(e, 'There was a problem loading the tree permits.');
        setNotification('error', err);
    };

    render() {
        const {
            seasons,
            treePermit,
            permitTypes,
            rating,
            treePermitObj: { loaded: isLoadedPermit },
            seasonsObj: { loaded: isLoadedSeason },
            availability,
        } = this.props;
        const { parentLocation } = this.state;

        if (isLoadedPermit && (!treePermit || !treePermit?.id)) {
            return <NotFoundPage />;
        }

        return (
            <div className="tp-details-page">
                {/* Fire the view_item GA Event */}
                <SendGAEvent
                    itemLevel="Facility"
                    itemCategory={INVENTORY_TREE_PERMIT}
                    facilityId={treePermit?.asset_id}
                    isDataLoading={!isLoadedPermit}
                />

                <Banner />
                <NotificationContainer className={'mb-0 mx-6 mt-4'} />
                <ContentBlock className="mt-5 mb-5" role="main">
                    <Stack>
                        <Header
                            treePermit={treePermit}
                            rating={rating}
                            parentLocation={parentLocation}
                            isLoading={!isLoadedPermit}
                        />
                        <CTABar
                            treePermitId={treePermit?.id}
                            treePermit={treePermit}
                            permitTypes={permitTypes}
                            availability={availability}
                        />
                        <Content
                            treePermit={treePermit}
                            permitTypes={permitTypes}
                            seasons={seasons}
                            rating={rating}
                            isLoaded={isLoadedSeason}
                        />
                    </Stack>
                </ContentBlock>
            </div>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    treePermit: locationsSel.getPermit(state) as TreePermitData,
    seasons: seasonSel.getSeasons(state) as Record<string, SeasonData>,
    permitTypes: typesSel.getPermitTypes(state) as PermitType[],
    availability: availabilitySel.getAvailability(state) as Record<
        string,
        Partial<AvailabilityEntry>
    >,
    treePermitObj: get(state, 'treePermit.permit', {
        loading: false,
        loaded: false,
        error: null,
        data: null,
    }) as AsyncState,
    seasonsObj: get(state, 'treePermit.seasons', {
        loading: false,
        loaded: false,
        error: null,
        data: null,
    }) as AsyncState,
    rating: get(state, 'ratingReviews.fetchAggregate.data', {}) as RatingData,
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
    bindActionCreators(
        {
            getTreePermit: fetchPermit,
            getPermitTypes: fetchPermitTypes,
            getAvailability: fetchAvailability,
            getSeasons: fetchSeasons,
            setNotification: NotificationActions.setNotification,
        },
        dispatch
    );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withCompatRouter(TreePermitDetailsPage) as any);
